import { useState, useMemo } from "react";
import {
  Tab,
  ObjectType,
  tableFieldHeight,
  tableHeaderHeight,
  tableColorStripHeight,
  defaultBlue
} from "../../data/constants";
import {
  EditOutlined,
  MoreOutlined,
  MinusOutlined,
  DeleteOutlined,
  KeyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Tag as AntdTag } from "antd";
import { Popover, Button, Drawer, Typography } from "antd";
import {
  useLayout,
  useSettings,
  useDiagram,
  useSelect,
  useUndoRedo,
} from "../../hooks";
import TableInfo from "../EditorSidePanel/TablesTab/TableInfo";
import { useTranslation } from "react-i18next";
import { dbToTypes } from "../../data/datatypes";
import { isRtl } from "../../i18n/utils/rtl";
import i18n from "../../i18n/i18n";
import ColorPalette from "../ColorPicker";

const { Paragraph } = Typography;

export default function Table(props) {
  const [hoveredField, setHoveredField] = useState(-1);
  const { database, updateTable } = useDiagram();
  const {
    tableData,
    onPointerDown,
    setHoveredTable,
    handleGripField,
    setLinkingLine,
  } = props;
  const { layout } = useLayout();
  const { deleteTable, deleteField } = useDiagram();
  const { settings } = useSettings();
  const { t } = useTranslation();
  const { selectedElement, setSelectedElement, bulkSelectedElements } =
    useSelect();
  const [editField, setEditField] = useState({});
  const { setUndoStack, setRedoStack } = useUndoRedo();

  const borderColor = useMemo(
    () => (settings.mode === "light" ? "border-zinc-300" : "border-zinc-600"),
    [settings.mode],
  );
  const height =
    tableData.fields.length * tableFieldHeight + tableHeaderHeight + 7;

  const isSelected = useMemo(() => {
    return (
      (selectedElement.id === tableData.id &&
        selectedElement.element === ObjectType.TABLE) ||
      bulkSelectedElements.some(
        (e) => e.type === ObjectType.TABLE && e.id === tableData.id,
      )
    );
  }, [selectedElement, tableData, bulkSelectedElements]);
  const openEditor = () => {
    if (!layout.sidebar) {
      setSelectedElement((prev) => ({
        ...prev,
        element: ObjectType.TABLE,
        id: tableData.id,
        open: true,
      }));
    } else {
      setSelectedElement((prev) => ({
        ...prev,
        currentTab: Tab.TABLES,
        element: ObjectType.TABLE,
        id: tableData.id,
        open: true,
      }));
      if (selectedElement.currentTab !== Tab.TABLES) return;
      document
        .getElementById(`scroll_table_${tableData.id}`)
        .scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <foreignObject
        key={tableData.id}
        x={tableData.x}
        y={tableData.y}
        width={settings.tableWidth}
        height={height}
        className="group drop-shadow-lg rounded-md cursor-move"
        onPointerDown={onPointerDown}
      >
        <div
          onDoubleClick={openEditor}
          className={`border-2 hover:border-dashed hover:border-blue-500
               select-none rounded-lg w-full 
               ${
                 selectedElement.id === tableData.id &&
                 selectedElement.element === ObjectType.TABLE
                   ? "border-solid border-blue-500"
                   : "border-zinc-500"
               } 
               ${isSelected ? "border-solid border-blue-500" : borderColor}`}
          style={{
            direction: "ltr",
            border: "1px solid transparent",
            background:
              "linear-gradient(rgb(255, 255, 255), rgb(255, 255, 255)) padding-box padding-box, linear-gradient(90deg, rgb(222, 209, 255) 0%, rgb(181, 245, 236) 100%) border-box border-box",
          }}
        >
          {/* <div
            className="h-[10px] w-full rounded-t-md"
            style={{ backgroundColor: tableData.color }}
          /> */}
          <div
            className={`overflow-hidden font-bold h-[40px] flex justify-between items-center border-b border-gray-400 rounded-t-lg ${
              settings.mode === "light" ? "bg-white" : "bg-zinc-900"
            }`}
            style={{
              background: `linear-gradient(180deg, ${tableData.color} -1.16%, #FFF 100%)`,
            }}
          >
            <div className=" px-3 overflow-hidden text-ellipsis whitespace-nowrap">
              {tableData.name}
            </div>
            <div className="hidden group-hover:block">
              <div className="flex justify-end items-center mx-2">
                {/* <Button
                  icon={<EditOutlined />}
                  size="small"
                  theme="solid"
                  style={{
                    backgroundColor: "#2f68adb3",
                    marginRight: "6px",
                  }}
                  onClick={openEditor}
                /> */}
                <Popover
                  key={tableData.key}
                  content={
                    <div className="popover-theme">
                      <div className="mb-2">
                        <strong>{t("comment")}:</strong>{" "}
                        {tableData.comment === "" ? (
                          t("not_set")
                        ) : (
                          <div className="bg-slate-100 rounded-lg px-2 py-1">
                            {tableData.comment}
                          </div>
                        )}
                      </div>
                      <div>
                        <strong
                          className={`${
                            tableData.indices.length === 0 ? "" : "block"
                          }`}
                        >
                          {t("indices")}:
                        </strong>{" "}
                        {tableData.indices.length === 0 ? (
                          t("not_set")
                        ) : (
                          <div className="bg-slate-100 rounded-lg">
                            {tableData.indices.map((index, k) => (
                              <div
                                key={k}
                                className={`flex items-center my-1 px-2 py-1 rounded bg-slate-100`}
                              >
                                {/* <i className="fa-solid fa-thumbtack me-2 mt-1 text-slate-500"></i> */}
                                <div>
                                  {index.fields.map((f) => (
                                    <AntdTag
                                      color="blue"
                                      key={f}
                                      className="me-1"
                                    >
                                      {f}
                                    </AntdTag>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  }
                  placement="rightTop"
                  arrow
                  trigger="click"
                  style={{ width: "200px", wordBreak: "break-word" }}
                >
                  <ExclamationCircleOutlined
                    style={{ marginLeft: 8, fontSize: 16, color: "green" }}
                  />
                </Popover>
                <DeleteOutlined
                  style={{ marginLeft: 8, fontSize: 16, color: "red" }}
                  onClick={() => deleteTable(tableData.id)}
                />
              </div>
            </div>
          </div>
          {tableData.fields.map((e, i) => {
            return settings.showFieldSummary ? (
              <Popover
                key={i}
                content={
                  <div className="popover-theme">
                    <div
                      className="flex justify-between items-center pb-2"
                      style={{ direction: "ltr" }}
                    >
                      <p className="me-4 font-bold">{e.name}</p>
                      <p className="ms-4">
                        {e.type +
                          ((dbToTypes[database][e.type].isSized ||
                            dbToTypes[database][e.type].hasPrecision) &&
                          e.size &&
                          e.size !== ""
                            ? "(" + e.size + ")"
                            : "")}
                      </p>
                    </div>
                    <hr />
                    {e.primary && (
                      <AntdTag
                        color="blue"
                        className="me-2 my-2"
                        style={{ margin: "8px 0", marginInlineEnd: "8px" }}
                      >
                        {t("primary")}
                      </AntdTag>
                    )}
                    {e.unique && (
                      <AntdTag
                        color="orange"
                        className="me-2 my-2"
                        style={{ margin: "8px 0", marginInlineEnd: "8px" }}
                      >
                        {t("unique")}
                      </AntdTag>
                    )}
                    {e.notNull && (
                      <AntdTag
                        color="purple"
                        className="me-2 my-2"
                        style={{ margin: "8px 0", marginInlineEnd: "8px" }}
                      >
                        {t("not_null")}
                      </AntdTag>
                    )}
                    {e.increment && (
                      <AntdTag
                        color="green"
                        className="me-2 my-2"
                        style={{ margin: "8px 0", marginInlineEnd: "8px" }}
                      >
                        {t("autoincrement")}
                      </AntdTag>
                    )}

                    <p>
                      <strong>{t("default_value")}: </strong>
                      {e.default === "" ? t("not_set") : e.default}
                    </p>
                    <p>
                      <strong>{t("comment")}: </strong>
                      {e.comment === "" ? t("not_set") : e.comment}
                    </p>
                  </div>
                }
                placement="right"
                arrow
                style={
                  isRtl(i18n.language)
                    ? { direction: "rtl" }
                    : { direction: "ltr" }
                }
              >
                {field(e, i)}
              </Popover>
            ) : (
              field(e, i)
            );
          })}
        </div>
      </foreignObject>
      <Drawer
        title={
          <div className="flex items-center">
            <Paragraph
              editable={{
                onChange: (value) => {
                  updateTable(tableData.id, { name: value });
                  setUndoStack((prev) => [
                    ...prev,
                    {
                      action: Action.EDIT,
                      element: ObjectType.TABLE,
                      component: "self",
                      tid: tableData.id,
                      undo: editField,
                      redo: { name: value },
                      message: t("edit_table", {
                        tableName: value,
                        extra: "[name]",
                      }),
                    },
                  ]);
                  setRedoStack([]);
                },
                onStart: () => setEditField({ name: tableData.name }),
              }}
              className="ms-2"
              style={{
                color: tableData.name.trim() === "" ? "#ff4d4f" : "inherit",
                margin: 0,
                flex: 1,
              }}
            >
              {tableData.name || t("name")}
            </Paragraph>
            <div className="flex items-center mr-3">
              <Popover
                content={
                  <div className="popover-theme">
                    <ColorPalette
                      currentColor={tableData.color}
                      onClearColor={() => {
                        setUndoStack((prev) => [
                          ...prev,
                          {
                            action: Action.EDIT,
                            element: ObjectType.TABLE,
                            component: "self",
                            tid: tableData.id,
                            undo: { color: tableData.color },
                            redo: { color: defaultBlue },
                            message: t("edit_table", {
                              tableName: tableData.name,
                              extra: "[color]",
                            }),
                          },
                        ]);
                        setRedoStack([]);
                        updateTable(tableData.id, { color: defaultBlue });
                      }}
                      onPickColor={(c) => {
                        setUndoStack((prev) => [
                          ...prev,
                          {
                            action: Action.EDIT,
                            element: ObjectType.TABLE,
                            component: "self",
                            tid: tableData.id,
                            undo: { color: tableData.color },
                            redo: { color: c },
                            message: t("edit_table", {
                              tableName: tableData.name,
                              extra: "[color]",
                            }),
                          },
                        ]);
                        setRedoStack([]);
                        updateTable(tableData.id, { color: c });
                      }}
                    />
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
                arrow
              >
                <div
                  className="h-[32px] w-[32px] rounded"
                  style={{ backgroundColor: tableData.color }}
                />
              </Popover>
            </div>
          </div>
        }
        size="small"
        open={
          selectedElement.element === ObjectType.TABLE &&
          selectedElement.id === tableData.id &&
          selectedElement.open &&
          !layout.sidebar
        }
        onClose={() =>
          setSelectedElement((prev) => ({
            ...prev,
            open: !prev.open,
          }))
        }
        style={{ paddingBottom: "16px" }}
        width={600}
        className="drawer-custom-padding custom-drawer-header"
      >
        <div className="sidesheet-theme">
          <TableInfo data={tableData} />
        </div>
      </Drawer>
    </>
  );

  function field(fieldData, index) {
    return (
      <div
        key={index}
        className={`${
          index === tableData.fields.length - 1
            ? "rounded-b-lg"
            : "border-b border-gray-400"
        } group h-[36px] px-2 py-1 flex justify-between items-center gap-1 w-full overflow-hidden bg-white`}
        onPointerEnter={(e) => {
          if (!e.isPrimary) return;

          setHoveredField(index);
          setHoveredTable({
            tableId: tableData.id,
            field: index,
          });
        }}
        onPointerLeave={(e) => {
          if (!e.isPrimary) return;

          setHoveredField(-1);
        }}
        onPointerDown={(e) => {
          // Required for onPointerLeave to trigger when a touch pointer leaves
          // https://stackoverflow.com/a/70976017/1137077
          e.target.releasePointerCapture(e.pointerId);
        }}
      >
        <div
          className={`${
            hoveredField === index ? "text-zinc-400" : ""
          } flex items-center gap-2 overflow-hidden`}
        >
          <button
            className="flex-shrink-0 w-[10px] h-[10px] bg-[#2f68adcc] rounded-full"
            onPointerDown={(e) => {
              if (!e.isPrimary) return;

              handleGripField(index);
              setLinkingLine((prev) => ({
                ...prev,
                startFieldId: index,
                startTableId: tableData.id,
                startX: tableData.x + 15,
                startY:
                  tableData.y +
                  index * tableFieldHeight +
                  tableHeaderHeight +
                  tableColorStripHeight +
                  12,
                endX: tableData.x + 15,
                endY:
                  tableData.y +
                  index * tableFieldHeight +
                  tableHeaderHeight +
                  tableColorStripHeight +
                  12,
              }));
            }}
          />
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {fieldData.name}
          </span>
        </div>
        <div className="text-zinc-400">
          {hoveredField === index ? (
            <Button
              type="primary"
              size="small"
              style={{
                backgroundColor: "#d42020b3",
              }}
              icon={<MinusOutlined />}
              onClick={() => deleteField(fieldData, tableData.id)}
            />
          ) : (
            <div className="flex gap-1 items-center">
              {fieldData.primary && <KeyOutlined />}
              {!fieldData.notNull && <span>?</span>}
              <AntdTag>
                {" "}
                {fieldData.type +
                  ((dbToTypes[database][fieldData.type].isSized ||
                    dbToTypes[database][fieldData.type].hasPrecision) &&
                  fieldData.size &&
                  fieldData.size !== ""
                    ? "(" + fieldData.size + ")"
                    : "")}
              </AntdTag>
              {/* <span>
                {fieldData.type +
                  ((dbToTypes[database][fieldData.type].isSized ||
                    dbToTypes[database][fieldData.type].hasPrecision) &&
                  fieldData.size &&
                  fieldData.size !== ""
                    ? "(" + fieldData.size + ")"
                    : "")}
              </span> */}
            </div>
          )}
        </div>
      </div>
    );
  }
}
